import { AppBar, IconButton, Toolbar, Button, useTheme, useMediaQuery } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import type { SxProps, Theme } from '@mui/material/styles'
import { NAVBAR_HEIGHT_DESKTOP, NAVBAR_HEIGHT_MOBILE } from '../Config/layoutConstants'

type TabType = 'overview' | 'valuation' | 'growth'

interface NavBarProps {
  selectedTab: TabType
  onTabChange: (tab: TabType) => void
  onDrawerToggle: () => void
}

// NavBar styling constants
const BUTTON_FONT_SIZE_MOBILE = '0.813rem' // 13px
const BUTTON_FONT_SIZE_DESKTOP = '0.875rem' // 14px
const BUTTON_PADDING_MOBILE = 1 // spacing units (8px)
const BUTTON_PADDING_DESKTOP = 1.5 // spacing units (12px)
const BUTTON_MIN_WIDTH_DESKTOP = '64px'
const ICON_BUTTON_MARGIN_MOBILE = 0.5 // spacing units (4px)
const ICON_BUTTON_MARGIN_DESKTOP = 1 // spacing units (8px)
const SELECTED_TAB_BACKGROUND = 'rgba(255, 255, 255, 0.2)'

const NavBar: React.FC<NavBarProps> = ({ selectedTab, onTabChange, onDrawerToggle }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getButtonSx = (tab: TabType): SxProps<Theme> => ({
    backgroundColor: selectedTab === tab ? SELECTED_TAB_BACKGROUND : 'transparent',
    fontWeight: selectedTab === tab ? 'bold' : 'normal',
    fontSize: isMobile ? BUTTON_FONT_SIZE_MOBILE : BUTTON_FONT_SIZE_DESKTOP,
    px: isMobile ? BUTTON_PADDING_MOBILE : BUTTON_PADDING_DESKTOP,
    minWidth: isMobile ? 'auto' : BUTTON_MIN_WIDTH_DESKTOP,
  });

  const iconButtonSx: SxProps<Theme> = {
    mr: isMobile ? ICON_BUTTON_MARGIN_MOBILE : ICON_BUTTON_MARGIN_DESKTOP,
  };

  return (
    <>
      <AppBar position="fixed" sx={{ color: 'primary.dark' }}>
        <Toolbar 
          variant={isMobile ? 'dense' : 'regular'}
          sx={{ minHeight: isMobile ? `${NAVBAR_HEIGHT_MOBILE}px !important` : `${NAVBAR_HEIGHT_DESKTOP}px !important` }}
        >
          <IconButton 
            edge="start" 
            color="inherit" 
            aria-label="menu"
            onClick={onDrawerToggle}
            size={isMobile ? 'small' : 'medium'}
            sx={iconButtonSx}
          >
            <MenuIcon fontSize={isMobile ? 'small' : 'medium'} />
          </IconButton>
          <Button 
            color="inherit" 
            onClick={() => onTabChange('overview')}
            sx={getButtonSx('overview')}
          >
            Overview
          </Button>
          <Button 
            color="inherit" 
            onClick={() => onTabChange('valuation')}
            sx={getButtonSx('valuation')}
          >
            Valuation
          </Button>
          <Button 
            color="inherit" 
            onClick={() => onTabChange('growth')}
            sx={getButtonSx('growth')}
          >
            Growth
          </Button>
        </Toolbar>
      </AppBar>
      {/*Prevent content being hidden by AppBar by adding dummy Toolbar*/}
      <Toolbar 
        variant={isMobile ? 'dense' : 'regular'}
        sx={{ minHeight: isMobile ? `${NAVBAR_HEIGHT_MOBILE}px !important` : `${NAVBAR_HEIGHT_DESKTOP}px !important` }}
      />
    </>
  );
};

export default NavBar;